import { AlertTriangle, Clock, ShieldCheck, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@ui/lib/utils';

export type VerificationBannerState = 'submit' | 'under-review' | 'rejected';

export interface VerificationBannerTranslations {
  submitTitle: string;
  submitDescription: string;
  submitCta: string;
  underReviewTitle: string;
  underReviewDescription: string;
  rejectedTitle: string;
  /** Fallback when no specific rejection reason is available. */
  rejectedDescription: string;
  rejectedCta: string;
}

export interface VerificationBannerProps {
  state: VerificationBannerState;
  translations: VerificationBannerTranslations;
  /**
   * Rendered inside the CTA container — typically a next/link so routing stays
   * within the consumer app. Only shown for `submit` and `rejected` states.
   */
  ctaSlot?: ReactNode;
  /**
   * Optional admin-provided rejection reason. Used in the `rejected` state to
   * replace the generic description.
   */
  rejectionReason?: string;
  /**
   * When provided, a dismiss (×) button is rendered. The consumer owns the
   * persistence (e.g. session-scoped) and re-show logic.
   */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button. Required when `onDismiss` is set. */
  dismissLabel?: string;
  className?: string;
}

/**
 * Sitewide nudge for professionals who have not yet been approved by the
 * moderation team. It is intentionally anchored on the initial approval state
 * only — once `professional.status === 'approved'`, the hosting app should
 * not render this component at all, even while a revision is pending.
 *
 * This component is purely presentational; data fetching and state derivation
 * live in the consumer.
 */
export function VerificationBanner({
  state,
  translations,
  ctaSlot,
  rejectionReason,
  onDismiss,
  dismissLabel,
  className
}: VerificationBannerProps) {
  const config = getStateConfig(state);

  const title = translations[config.titleKey];
  const description = state === 'rejected' && rejectionReason ? rejectionReason : translations[config.descriptionKey];

  return (
    <div role="status" className={cn('w-full border-b py-3', config.toneClasses, className)}>
      <div className="layout-bounds flex flex-row items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="flex-shrink-0 pt-0.5" aria-hidden="true">
            {config.icon}
          </span>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm font-semibold leading-tight">{title}</span>
            <span className="text-sm opacity-90 leading-snug">{description}</span>
          </div>
        </div>
        {ctaSlot && <div className={cn('flex-shrink-0', !onDismiss && 'ml-auto')}>{ctaSlot}</div>}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className={cn(
              'flex-shrink-0 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1',
              !ctaSlot && 'ml-auto'
            )}
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

interface StateConfig {
  toneClasses: string;
  icon: ReactNode;
  titleKey: keyof VerificationBannerTranslations;
  descriptionKey: keyof VerificationBannerTranslations;
}

function getStateConfig(state: VerificationBannerState): StateConfig {
  switch (state) {
    case 'submit':
      return {
        toneClasses: 'bg-amber-50 border-amber-200 text-amber-900',
        icon: <ShieldCheck className="size-5" />,
        titleKey: 'submitTitle',
        descriptionKey: 'submitDescription'
      };
    case 'under-review':
      return {
        toneClasses: 'bg-blue-50 border-blue-200 text-blue-900',
        icon: <Clock className="size-5" />,
        titleKey: 'underReviewTitle',
        descriptionKey: 'underReviewDescription'
      };
    case 'rejected':
      return {
        toneClasses: 'bg-red-50 border-red-200 text-red-900',
        icon: <AlertTriangle className="size-5" />,
        titleKey: 'rejectedTitle',
        descriptionKey: 'rejectedDescription'
      };
  }
}
